import { metaSchemas } from "@/lib/constants";
import {
  channelsSort,
  genreSort,
  searchSort,
  searchTvShowSort,
  titlesSort,
} from "@/lib/sorts";
import merge from "deepmerge";
import LogicaAPI from "./logicaapi";

class Meta extends LogicaAPI {
  howtoPlay(id) {
    const params = {
      app_id: this.config.appId,
      device_code: this.config.deviceCode,
      with_howto_acquisition: true,
      expand_object_flag: true,
    };
    return this.get(`/api/v1/metas/${id}/howto_play`, { params });
  }

  children({ id, offset, limit, filter = {}, sort = [] }) {
    const params = {
      ...this.dsearchCommonParams,
      expand_object_flag: false,
      with_total_count: true,
      condition: merge(
        {
          meta_schema_id: metaSchemas.episode.id,
          "values.tv_show": id,
          time_range: {
            delivery_end_at: { gt: "now" },
          },
          ...this.dsearchCommonCondition,
        },
        filter
      ),
      sort: merge(
        [{ "values.num": "asc" }, { delivery_start_at: "asc" }, { id: "desc" }],
        sort
      ),
      from: offset,
      limit,
    };
    return this.get(`/api/v1/metas/dsearch`, {
      params: {
        q: JSON.stringify(params),
      },
    });
  }

  channels({ offset, limit, filter = {}, sort = "pr" }) {
    let sortOrder;
    if (sort == "mylist") {
      sortOrder = { sort: [{ entitled_at: "desc" }, { id: "desc" }] };
    } else {
      sortOrder = channelsSort.find((v) => v.value == sort);
    }
    const params = {
      ...this.dsearchCommonParams,
      expand_object_flag: false,
      with_total_count: true,
      condition: merge(
        {
          meta_schema_id: metaSchemas.channel.id,
          ...this.dsearchCommonCondition,
        },
        filter
      ),
      sort: sortOrder.sort,
      from: offset,
      limit,
    };

    return this.get(`/api/v1/metas/dsearch`, {
      params: {
        q: JSON.stringify(params),
      },
    });
  }

  channelFeatureEpisodes({ id, offset, limit }) {
    // vod、live、見逃し全て含める
    const params = {
      ...this.dsearchCommonParams,
      expand_object_flag: false,
      with_total_count: true,
      condition: {
        meta_schema_id: metaSchemas.episode.id,
        "values.channel": id,
        time_range: {
          delivery_end_at: { gt: "now" },
        },
        ...this.dsearchCommonCondition,
      },
      sort: [{ push_rank: "desc" }, { name: "asc" }, { id: "desc" }],
      from: offset,
      limit,
    };
    return this.get(`/api/v1/metas/dsearch`, {
      params: {
        q: JSON.stringify(params),
      },
    });
  }

  async channelDetail(id) {
    const params = {
      ...this.dsearchCommonParams,
      condition: {
        meta_schema_id: metaSchemas.channel.id,
        id,
        ...this.dsearchCommonCondition,
      },
    };
    const res = await this.get(`/api/v1/metas/dsearch`, {
      params: {
        q: JSON.stringify(params),
      },
    });

    return res?.metas?.[0];
  }

  async searchEpisode({
    keyword,
    filter = {},
    sort = "rel",
    offset,
    limit,
    minScore = null,
  }) {
    const sortOrder = searchSort.find((v) => v.value == sort);
    const params = {
      ...this.dsearchCommonParams,
      with_total_count: true,
      keyword,
      condition: merge(
        {
          meta_schema_id: metaSchemas.episode.id,
          time_range: {
            delivery_end_at: { gt: "now" },
          },
          ...this.dsearchCommonCondition,
        },
        filter
      ),
      sort: sortOrder.sort,
      from: offset,
      limit,
    };
    if (!!minScore) {
      params.min_score = minScore;
    }
    return await this.get("/api/v1/metas/dsearch", {
      params: {
        q: JSON.stringify(params),
      },
    });
  }

  async searchTvShow({
    keyword,
    filter = {},
    sort = "rel",
    offset,
    limit,
    minScore = null,
  }) {
    const sortOrder = searchTvShowSort.find((v) => v.value == sort);
    const params = {
      ...this.dsearchCommonParams,
      with_total_count: true,
      keyword,
      condition: merge(
        {
          meta_schema_id: metaSchemas.tvshow.id,
          ...this.dsearchCommonCondition,
        },
        filter
      ),
      sort: sortOrder.sort,
      from: offset,
      limit,
    };
    if (!!minScore) {
      params.min_score = minScore;
    }
    return await this.get("/api/v1/metas/dsearch", {
      params: {
        q: JSON.stringify(params),
      },
    });
  }

  channelLiveSchedule({ id, day, offset, limit }) {
    const params = {
      ...this.dsearchCommonParams,
      expand_object_flag: false,
      with_total_count: true,
      condition: {
        meta_schema_id: metaSchemas.episode.id,
        "values.channel": id,
        time_range: {
          delivery_start_at: { gte: `${day} 00:00:00`, lte: `${day} 23:59:59` },
        },
        ...this.dsearchCommonCondition,
      },
      sort: [
        { delivery_start_at: "asc" },
        { push_rank: "desc" },
        { id: "desc" },
      ],
      from: offset,
      limit,
    };
    return this.get(`/api/v1/metas/dsearch`, {
      params: {
        q: JSON.stringify(params),
      },
    });
  }

  async channelLinearEpisodes({ id, offset, limit, filter = {} }) {
    const params = {
      ...this.dsearchCommonParams,
      expand_object_flag: false,
      with_total_count: true,
      condition: merge(
        {
          meta_schema_id: metaSchemas.episode.id,
          "values.channel": id,
          "values.is_live": true,
          time_range: {
            delivery_end_at: { gt: "now" },
            "values.encode_end_date": { gt: "now" },
          },
          not: {
            "values.source_epg_id": null,
          },
          ...this.dsearchCommonCondition,
        },
        filter
      ),
      sort: [{ delivery_start_at: "asc" }, { id: "desc" }],
      from: offset,
      limit,
    };
    return await this.get(`/api/v1/metas/dsearch`, {
      params: {
        q: JSON.stringify(params),
      },
    });
  }

  async title(id) {
    const params = {
      ...this.dsearchCommonParams,
      condition: {
        meta_schema_id: metaSchemas.tvshow.id,
        id,
        ...this.dsearchCommonCondition,
      },
    };
    const res = await this.get(`/api/v1/metas/dsearch`, {
      params: {
        q: JSON.stringify(params),
      },
    });

    return res?.metas?.[0];
  }

  async titles({ filter = {}, sort = "pr", offset, limit }) {
    const sortOrder = titlesSort.find((v) => v.value === sort);
    const params = {
      ...this.dsearchCommonParams,
      with_total_count: true,
      condition: merge(
        {
          meta_schema_id: metaSchemas.tvshow.id,
          ...this.dsearchCommonCondition,
        },
        filter
      ),
      sort: sortOrder.sort,
      from: offset,
      limit,
    };
    return this.get(`/api/v1/metas/dsearch`, {
      params: {
        q: JSON.stringify(params),
      },
    });
  }

  entitled({ channelId, contentType, offset, limit }) {
    const params = {
      ...this.dsearchCommonParams,
      condition: {
        is_entitled: true,
        time_range: {
          delivery_end_at: { gt: "now" },
        },
        ...this.dsearchCommonCondition,
        ...this.dsearchContentTypeCondition(contentType),
      },
      from: offset,
      limit: limit,
      sort: [{ entitled_at: "desc" }, { "values.num": "asc" }, { id: "desc" }],
      with_total_count: true,
    };
    if (channelId) {
      params.condition["values.channel"] = channelId;
    }
    return this.get(`/api/v1/metas/dsearch`, {
      params: {
        q: JSON.stringify(params),
      },
    });
  }

  getEntitledStoreEpisodes(args) {
    const params = {
      ...this.dsearchCommonParams,
      condition: {
        meta_schema_id: metaSchemas.episode.id,
        is_entitled: true,
        time_range: {
          delivery_end_at: { gt: "now" },
        },
        "values.channel": this.config.storeId,
        ...this.dsearchCommonCondition,
      },
      from: args.offset,
      limit: args.limit,
      sort: [{ entitled_at: "desc" }, { "values.num": "asc" }, { id: "desc" }],
      with_total_count: true,
    };
    const res = this.get(`/api/v1/metas/dsearch`, {
      params: {
        q: JSON.stringify(params),
      },
    });

    return res;
  }

  genrePaletteEpisodes({ channel, genre, offset, limit, sortOrder, type }) {
    const sort = genreSort.find((v) => v.value == sortOrder);
    const params = {
      ...this.dsearchCommonParams,
      condition: {
        meta_schema_id: metaSchemas.episode.id,
        ...this.dsearchCommonCondition,
      },
      from: offset,
      limit: limit,
      sort: sort.sort,
      with_total_count: true,
    };
    if (channel) {
      params.condition["values.channel"] = channel;
    }
    if (genre && genre.length > 0 && type === "id") {
      params.condition["values.genres"] = genre;
    } else if (genre && genre.length > 0 && type === "not-id") {
      params.condition["not"] = {
        "values.genres": genre,
      };
    }

    const res = this.get(`/api/v1/metas/dsearch`, {
      params: {
        q: JSON.stringify(params),
      },
    });
    return res;
  }

  searchFromId(ids, limit) {
    const params = {
      ...this.dsearchCommonParams,
      condition: {
        id: ids,
        ...this.dsearchCommonCondition,
      },
      limit,
    };
    return this.get(`/api/v1/metas/dsearch`, {
      params: {
        q: JSON.stringify(params),
      },
    });
  }

  searchFromRightId(rightId) {
    const params = {
      ...this.dsearchCommonParams,
      condition: {
        meta_schema_id: metaSchemas.episode.id,
        nested: {
          path: "right_metas",
          condition: {
            "right_metas.right_id": rightId,
          },
        },
      },
      limit: 1,
    };
    return this.get(`/api/v1/metas/dsearch`, {
      params: {
        q: JSON.stringify(params),
      },
    });
  }

  rankingSearch(channel, target, range, limit) {
    const order = (() => {
      if (range === "1week") {
        return "weekly_ranking";
      } else if (range === "1month") {
        return "monthly_ranking";
      } else if (range === "1day") {
        return "daily_ranking";
      }
    })();

    const params = {
      ...this.dsearchCommonParams,
      condition: {
        meta_schema_id: metaSchemas.episode.id,
        ...this.dsearchCommonCondition,
      },
      sort: [{ [target]: order }],
      limit,
    };
    if (channel) {
      params.condition["values.channel"] = channel;
    }
    return this.get(`/api/v1/metas/dsearch`, {
      params: {
        q: JSON.stringify(params),
      },
    });
  }

  async getRooms(metaId) {
    return this.get(`/api/v1/metas/${metaId}/meta_rooms`);
  }
}
export default Meta;
